import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// Same key Tracking map uses for the device-location dot
const LAST_KNOWN_LOCATION_KEY = 'lastKnownLocation';

const NOMINATIM_HEADERS = {
  Accept: 'application/json',
  'User-Agent': 'BusinessOS/1.0 (address-autocomplete; contact@example.com)'
};

const reverseGeocode = async (lat, lng) => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
      { headers: NOMINATIM_HEADERS }
    );
    const data = await res.json();
    const code = data.address?.country_code;
    const state = data.address?.state || data.address?.state_district || null;
    return { country: code ? code.toLowerCase() : null, state };
  } catch (_) {
    return { country: null, state: null };
  }
};

const getDeviceLocation = () =>
  new Promise((resolve) => {
    const done = (country, state) => resolve({ country: country ? country.toLowerCase() : null, state: state || null });
    const tryReverse = async (lat, lng) => {
      const out = await reverseGeocode(lat, lng);
      done(out.country, out.state);
    };
    const saved = localStorage.getItem(LAST_KNOWN_LOCATION_KEY);
    if (saved) {
      try {
        const [lat, lng] = JSON.parse(saved);
        if (typeof lat === 'number' && typeof lng === 'number') {
          tryReverse(lat, lng);
          return;
        }
      } catch (_) {}
    }
    if (!navigator.geolocation) {
      done(null, null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => tryReverse(pos.coords.latitude, pos.coords.longitude),
      () => done(null, null),
      { timeout: 8000, maximumAge: 600000 }
    );
  });

// Parse user query: "24 alma" -> { number: "24", streetPrefix: "alma" }
const parseUserQuery = (query) => {
  const q = (query || '').trim();
  if (!q) return { number: null, streetPrefix: '' };
  const numMatch = q.match(/^(\d+[a-zA-Z]*)\s+/);
  const userNumber = numMatch ? numMatch[1].replace(/\D/g, '') : q.match(/^(\d+)/)?.[1] || null;
  let rest = numMatch ? q.slice(numMatch[0].length).trim() : q;
  rest = rest.replace(/\d/g, '').trim().toLowerCase();
  const streetPrefix = rest.replace(/\b(street|st|road|rd|ave|avenue|drive|dr|lane|ln|place|pl|way)\s*$/i, '').trim();
  return { number: userNumber, streetPrefix: streetPrefix || '' };
};

// Extract street name from first segment: "24 Alma Road" -> "alma"
const getStreetNameFromSuggestion = (suggestion) => {
  const first = (suggestion.split(',')[0] || '').trim();
  const numMatch = first.match(/^(\d+[a-zA-Z]*)\s*/);
  const afterNumber = numMatch ? first.slice(numMatch[0].length).trim() : first;
  const roadTypes = /\b(street|st|road|rd|avenue|ave|drive|dr|lane|ln|place|pl|court|ct|parade|pde|way|hwy|crescent|cr|terrace|tce)\b/i;
  const words = afterNumber.split(/\s+/).filter(Boolean);
  const streetWords = [];
  for (const w of words) {
    if (roadTypes.test(w)) break;
    if (/[a-z]/i.test(w)) streetWords.push(w);
  }
  return streetWords.slice(0, 2).join(' ').toLowerCase() || afterNumber.toLowerCase();
};

// Require number, street, and suburb - filter out incomplete addresses
const hasNumberStreetSuburb = (suggestion) => {
  const parts = (suggestion || '').split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return false;
  const first = parts[0] || '';
  if (!/^\d/.test(first)) return false;
  const afterNum = first.replace(/^\d+[a-zA-Z]*\s*/, '').trim();
  if (!afterNum || !/[a-z]/i.test(afterNum)) return false;
  const suburbPart = parts.slice(1).find((p) => !/^\d{4}/.test(p) && /[a-z]/i.test(p));
  return !!suburbPart;
};

// Strict: number must match if given; street name must START with user's prefix (e.g. "alma" -> "Alma Road")
const isRelevantSuggestion = (suggestion, query) => {
  const { number: userNum, streetPrefix } = parseUserQuery(query);
  const text = suggestion.toLowerCase();
  if (userNum) {
    const first = (suggestion.split(',')[0] || '').trim();
    const suggNum = first.match(/^\d+/)?.[0];
    if (suggNum && suggNum !== userNum) return false;
  }
  if (streetPrefix) {
    const streetName = getStreetNameFromSuggestion(suggestion);
    if (!streetName.startsWith(streetPrefix)) return false;
  }
  return true;
};

// Minimal abbrev expansion for API - "main s" -> "main street", "24 alma" -> "24 alma road"
const expandAbbrevs = (q) => {
  let out = (q || '').trim();
  out = out
    .replace(/\b(\w+)\s+s\b$/i, '$1 street')
    .replace(/\b(\w+)\s+st\b$/i, '$1 street')
    .replace(/\b(\w+)\s+rd\b$/i, '$1 road')
    .replace(/\b(\w+)\s+r\b$/i, '$1 road');
  if (/^\d+\s+main\s*$/i.test(out)) out = out + ' street';
  if (/^\d+\s+alma\s*$/i.test(out)) out = out + ' road';
  return out || q;
};

const ensureNumberInSuggestion = (suggestionText, query) => {
  if (!suggestionText || !query) return suggestionText;
  const numMatch = query.trim().match(/^\s*(\d+[a-zA-Z]*)\s+/);
  const userNumber = numMatch ? numMatch[1].replace(/\D/g, '') : query.trim().match(/^(\d+)/)?.[1];
  if (!userNumber) return suggestionText;
  const firstSegment = suggestionText.split(',')[0]?.trim() || '';
  if (!firstSegment || /^\d/.test(firstSegment)) return suggestionText;
  const commaIdx = suggestionText.indexOf(',');
  const rest = commaIdx >= 0 ? suggestionText.substring(commaIdx + 1).trim() : '';
  return rest ? `${userNumber} ${firstSegment}, ${rest}` : `${userNumber} ${firstSegment}`;
};

const buildShortAddress = (comp, formattedFull) => {
  const parts = (formattedFull || '').split(',').map((p) => p.trim()).filter(Boolean);
  const streetLine = parts[0] || '';
  const place = comp?.suburb || comp?.village || comp?.town || comp?.city_district || comp?.city || comp?.locality || '';
  const postcode = comp?.postcode || '';
  if (streetLine && (place || postcode)) {
    return [streetLine, place, postcode].filter(Boolean).join(', ');
  }
  if (parts.length >= 3) return [parts[0], parts[1], parts[2]].join(', ');
  if (parts.length > 0) return parts.join(', ');
  if (!comp) return null;
  const num = comp.house_number || comp.house_name || comp.house || comp.building || comp.number || comp.housenumber || '';
  const road = comp.road || comp.street || comp.street_name || comp.pedestrian || '';
  const addressLine = [num, road].filter(Boolean).join(' ').trim();
  return [addressLine, place, postcode].filter(Boolean).join(', ') || (formattedFull || null);
};

// Format as: number, street, suburb, postcode. Handles Mapbox place_name (e.g. "Suburb VIC 3156").
const toTitleCase = (s) => (s || '').replace(/\b\w/g, (c) => c.toUpperCase());
const formatAddressOrder = (addr) => {
  if (!addr || typeof addr !== 'string') return addr;
  const raw = addr.trim();
  let commaParts = raw.split(',').map((p) => p.trim()).filter(Boolean);
  let postcode = commaParts.find((p) => /^\d{4}(-\d{4})?$/.test(p)) || '';
  if (!postcode) {
    const pcMatch = commaParts.find((p) => /\b(\d{4})(-\d{4})?$/.test(p));
    if (pcMatch) {
      postcode = pcMatch.match(/(\d{4}(-\d{4})?)\s*$/)?.[1] || '';
      commaParts = commaParts.map((p) => (p === pcMatch ? p.replace(/\s*\d{4}(-\d{4})?\s*$/, '').trim() : p));
    }
  }
  const rest = commaParts.filter((p) => p !== postcode && !/^\d{4}/.test(p));
  const streetPart = rest.find((p) => /^\d/.test(p)) || rest[0] || '';
  const suburbPart = rest.filter((p) => p !== streetPart)[0] || '';
  const out = [streetPart, suburbPart.replace(/\s+(VIC|NSW|QLD|SA|WA|TAS|NT|ACT)\s*$/i, '').trim(), postcode].filter(Boolean);
  return out.length ? toTitleCase(out.join(', ')) : raw;
};

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Backend proxy: ask our own API for suggestions to avoid browser CORS/rate limits.
const fetchBackendSuggestions = async (query) => {
  const q = expandAbbrevs((query || '').trim());
  const url = `${BASE_URL}/api/address-search?q=${encodeURIComponent(q)}`;
  const res = await fetch(url);
  if (!res.ok) return [];
  const data = await res.json().catch(() => ({}));
  return Array.isArray(data.suggestions) ? data.suggestions : [];
};

export function parseStateFromAddress(address) {
  if (!address || typeof address !== 'string') return null;
  const m = address.match(/\b([A-Z]{2})\s+\d{5}(-\d{4})?/);
  if (m) return m[1];
  const parts = address.split(',').map(p => p.trim());
  const twoLetter = parts.find(p => /^[A-Za-z]{2}$/.test(p));
  return twoLetter ? twoLetter.toUpperCase() : null;
}

const DEBOUNCE_MS = 300;
const BLUR_DELAY_MS = 250;
const MIN_QUERY_LEN = 2;

export default function AddressAutocomplete({ value, onChange, className, style, placeholder, stateOverride }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);
  const [deviceLocation, setDeviceLocation] = useState({ country: null, state: null });
  const deviceLocationFetched = useRef(false);
  const timeoutRef = useRef(null);
  const inputRef = useRef(null);
  const fetchIdRef = useRef(0);
  const lastQueryRef = useRef('');

  useEffect(() => {
    if (deviceLocationFetched.current) return;
    deviceLocationFetched.current = true;
    getDeviceLocation().then((loc) => {
      setDeviceLocation((prev) => (prev.country ? prev : { country: loc.country || null, state: loc.state || null }));
    });
  }, []);

  const updateDropdownRect = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownRect({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  };

  useEffect(() => {
    if (showSuggestions && (loading || suggestions.length > 0)) {
      updateDropdownRect();
    } else {
      setDropdownRect(null);
    }
  }, [showSuggestions, loading, suggestions.length]);

  const fetchSuggestions = async (query) => {
    const trimmed = (query || '').trim();
    lastQueryRef.current = trimmed;
    if (trimmed.length < MIN_QUERY_LEN) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    const myId = ++fetchIdRef.current;
    setLoading(true);
    try {
      let country = deviceLocation.country;
      let userState = stateOverride || deviceLocation.state || null;
      if (country == null) {
        const loc = await Promise.race([
          getDeviceLocation(),
          new Promise((r) => setTimeout(() => r({ country: null, state: null }), 3500))
        ]);
        country = loc.country;
        if (userState == null) userState = loc.state || null;
        setDeviceLocation((prev) => (prev.country ? prev : { country: loc.country || null, state: loc.state || null }));
      }
      // Fetch raw suggestions from backend proxy (which calls Nominatim server-side).
      let mbList = await fetchBackendSuggestions(trimmed);
      const seen = new Set();
      const list = [];
      for (const s of mbList) {
        const t = (s || '').trim();
        if (!t) continue;
        if (!isRelevantSuggestion(t, trimmed)) continue;
        const formatted = formatAddressOrder(ensureNumberInSuggestion(t, trimmed));
        if (!hasNumberStreetSuburb(formatted)) continue;
        const key = formatted.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        list.push(formatted);
      }
      if (myId === fetchIdRef.current && lastQueryRef.current === trimmed) setSuggestions(list);
    } catch (error) {
      if (myId === fetchIdRef.current) setSuggestions([]);
      console.error('Address autocomplete error:', error);
    } finally {
      if (myId === fetchIdRef.current) setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    lastQueryRef.current = newValue.trim();
    onChange(newValue);
    setShowSuggestions(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (newValue.trim().length >= MIN_QUERY_LEN) {
      timeoutRef.current = setTimeout(() => fetchSuggestions(newValue), DEBOUNCE_MS);
    } else {
      setSuggestions([]);
      setLoading(false);
    }
  };

  const handleFocus = () => {
    setShowSuggestions(true);
    const v = (value || '').trim();
    if (v.length >= MIN_QUERY_LEN) {
      lastQueryRef.current = v;
      fetchSuggestions(value);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    onChange(suggestion);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const showDropdown = showSuggestions && (loading || suggestions.length > 0);
  const dropdownContent = showDropdown && dropdownRect && createPortal(
    <div
      style={{
        position: 'fixed',
        top: dropdownRect.top,
        left: dropdownRect.left,
        width: dropdownRect.width,
        background: '#374151',
        border: '1px solid #4b5563',
        borderRadius: '8px',
        maxHeight: '200px',
        overflowY: 'auto',
        zIndex: 10000,
        boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
      }}
    >
      {loading ? (
        <div style={{ padding: '12px', color: '#9ca3af', fontSize: '13px' }}>Loading suggestions...</div>
      ) : (
        suggestions.map((suggestion, idx) => (
          <div
            key={idx}
            onMouseDown={(e) => { e.preventDefault(); handleSelectSuggestion(suggestion); }}
            style={{
              padding: '12px',
              cursor: 'pointer',
              borderBottom: idx < suggestions.length - 1 ? '1px solid #4b5563' : 'none',
              color: '#fff',
              fontSize: '14px'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#4b5563'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            📍 {suggestion}
          </div>
        ))
      )}
    </div>,
    document.body
  );

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={() => setTimeout(() => setShowSuggestions(false), BLUR_DELAY_MS)}
        className={className}
        style={style}
        placeholder={placeholder}
        autoComplete="off"
      />
      {dropdownContent}
    </div>
  );
}
